import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  ExpandOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { Switch, Space } from 'antd';
import { Frame, SegmentationLabel } from 'models/segmentation';
import { KeypointCollectionLabel, KeypointFrame } from 'models/keypoints';
import { Segmentation, Frame as FramePrediction } from 'models/predictions';
import KeypointOverlay from 'components/KeypointCollectionLabels/components/KeypointOverlay';
import SegmentationOverlayActions from '../../../ImageActions/ImageActions';
import SegHoverLayer from '../../../SegmentationLabels/components/SegHoverLayer';
import DifferenceOverlay from 'components/Predictions/components/DifferenceOverlay';
import { cleanDartPath, pointsToSvgPath } from '../../../../utils/segPath';
import {
  Wrapper,
  ControlsWrapper,
  ZoomDisplay,
  ImageGrid,
  ImageCard,
  ImageLabel,
  ImageContainer,
  StyledImage,
  StyledButton,
  TransformLayer,
} from './styled';

interface ZoomState {
  scale: number;
  x: number;
  y: number;
}

interface ImageComparatorProps {
  leftFrame?: Frame | KeypointFrame | FramePrediction;
  rightFrame?: Frame | KeypointFrame | FramePrediction;
  leftSegmentations?: SegmentationLabel[] | Segmentation[];
  rightSegmentations?: SegmentationLabel[] | Segmentation[];
  highlightedSegmentationClass?: string | null;
  shuffledSegmentations?: boolean;
  groundTruthSide?: 'left' | 'right' | null;
  onShuffleToggle?: (enabled: boolean) => void;
  onShuffle?: () => void;
  isBlinded?: boolean;
  highlightedSegmentationId?: string | null;
  highlightedSegmentationIdLeft?: string | null;
  highlightedSegmentationIdRight?: string | null;
  highlightedSegmentationPathsLeft?: string[] | null;
  highlightedSegmentationPathsRight?: string[] | null;
  highlightedSegmentationFillColorLeft?: string | null;
  highlightedSegmentationFillColorRight?: string | null;
  onHighlightedChange?: (icid: string | null) => void;
}

const ImageComparator: React.FC<ImageComparatorProps> = ({
  leftFrame,
  rightFrame,
  leftSegmentations = [],
  rightSegmentations = [],
  groundTruthSide = null,
  shuffledSegmentations = false,
  onShuffleToggle,
  onShuffle,
  isBlinded = false,
  highlightedSegmentationId = undefined,
  highlightedSegmentationIdLeft = undefined,
  highlightedSegmentationIdRight = undefined,
  highlightedSegmentationPathsLeft = undefined,
  highlightedSegmentationPathsRight = undefined,
  highlightedSegmentationClass = undefined,
  highlightedSegmentationFillColorLeft = undefined,
  highlightedSegmentationFillColorRight = undefined,
  onHighlightedChange = undefined,
}) => {
  const [visibleLeftSegmentations, setVisibleLeftSegmentations] = useState<Set<string>>(
    () => new Set(leftSegmentations.map(seg => seg.icid))
  );
  const [visibleRightSegmentations, setVisibleRightSegmentations] = useState<Set<string>>(
    () => new Set(rightSegmentations.map(seg => seg.icid))
  );
  const [visibleLeftKeypoints, setVisibleLeftKeypoints] = useState<Set<string>>(new Set());
  const [visibleRightKeypoints, setVisibleRightKeypoints] = useState<Set<string>>(new Set());
  const [zoomState, setZoomState] = useState<ZoomState>({ scale: 1, x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [leftImageDimensions, setLeftImageDimensions] = useState({ width: 0, height: 0 });
  const [rightImageDimensions, setRightImageDimensions] = useState({ width: 0, height: 0 });
  const [highlightedSegmentation, setHighlightedSegmentation] = useState<string | null>(() =>
    rightSegmentations && rightSegmentations.length > 0 ? (rightSegmentations[0] as any).icid : null
  );
  const [showDifference, setShowDifference] = useState<boolean>(false);

  useEffect(() => {
    if (
      highlightedSegmentationIdRight !== undefined &&
      highlightedSegmentationIdRight !== highlightedSegmentation
    ) {
      setHighlightedSegmentation(highlightedSegmentationIdRight || null);
    } else if (
      highlightedSegmentationId !== undefined &&
      highlightedSegmentationId !== highlightedSegmentation
    ) {
      setHighlightedSegmentation(highlightedSegmentationId || null);
    }
  }, [highlightedSegmentationIdRight, highlightedSegmentationId]);

  useEffect(() => {
    if (
      (!highlightedSegmentation || highlightedSegmentation === '') &&
      rightSegmentations &&
      rightSegmentations.length > 0
    ) {
      setHighlightedSegmentation((rightSegmentations[0] as any).icid);
    }
  }, [rightSegmentations]);

  useEffect(() => {
    if (onHighlightedChange) onHighlightedChange(highlightedSegmentation);
  }, [highlightedSegmentation, onHighlightedChange]);

  const leftContainerRef = useRef<HTMLDivElement>(null);
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const leftImageRef = useRef<HTMLImageElement>(null);
  const rightImageRef = useRef<HTMLImageElement>(null);

  const handleSegmentationToggle = useCallback(
    (icid: string, isVisible: boolean, side: 'left' | 'right') => {
      if (side === 'left') {
        setVisibleLeftSegmentations(prev => {
          const newSet = new Set(prev);
          if (isVisible) {
            newSet.add(icid);
          } else {
            newSet.delete(icid);
          }
          return newSet;
        });
      } else {
        setVisibleRightSegmentations(prev => {
          const newSet = new Set(prev);
          if (isVisible) {
            newSet.add(icid);
          } else {
            newSet.delete(icid);
          }
          return newSet;
        });
      }
    },
    []
  );

  useEffect(() => {
    if (leftFrame && 'keypointCollectionLabels' in leftFrame) {
      const keypointIds = new Set(leftFrame.keypointCollectionLabels.map(kp => kp.icid));
      setVisibleLeftKeypoints(keypointIds);
    }
  }, [leftFrame]);

  useEffect(() => {
    if (!leftSegmentations || leftSegmentations.length === 0) return;
    setVisibleLeftSegmentations(prev => {
      const next = new Set(prev);
      leftSegmentations.forEach(s => {
        if ((s as any)?.icid) next.add((s as any).icid);
      });
      return next;
    });
  }, [leftSegmentations]);

  useEffect(() => {
    if (rightFrame && 'keypointCollectionLabels' in rightFrame) {
      const keypointIds = new Set(rightFrame.keypointCollectionLabels.map(kp => kp.icid));
      setVisibleRightKeypoints(keypointIds);
    }
  }, [rightFrame]);

  useEffect(() => {
    if (!rightSegmentations || rightSegmentations.length === 0) return;
    setVisibleRightSegmentations(prev => {
      const next = new Set(prev);
      rightSegmentations.forEach(s => {
        if ((s as any)?.icid) next.add((s as any).icid);
      });
      return next;
    });
  }, [rightSegmentations]);

  const zoom = useCallback((factor: number, centerX?: number, centerY?: number) => {
    setZoomState(prev => {
      const newScale = Math.max(0.1, Math.min(10, prev.scale * factor));
      if (centerX !== undefined && centerY !== undefined) {
        const scaleDiff = newScale - prev.scale;
        const newX = prev.x - (centerX - prev.x) * (scaleDiff / prev.scale);
        const newY = prev.y - (centerY - prev.y) * (scaleDiff / prev.scale);
        return { scale: newScale, x: newX, y: newY };
      }
      return { ...prev, scale: newScale };
    });
  }, []);

  const resetZoom = useCallback(() => {
    setZoomState({ scale: 1, x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      setIsDragging(true);
      setDragStart({ x: e.clientX - zoomState.x, y: e.clientY - zoomState.y });
    },
    [zoomState.x, zoomState.y]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      setZoomState(prev => ({
        ...prev,
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      }));
    },
    [isDragging, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp);
      return () => window.removeEventListener('mouseup', handleMouseUp);
    }
  }, [isDragging, handleMouseUp]);

  const fitToScreen = useCallback(() => {
    if (!leftContainerRef.current || !leftImageRef.current) return;
    const container = leftContainerRef.current;
    const img = leftImageRef.current;
    if (!img.naturalWidth || !img.naturalHeight) return;
    const containerRatio = container.clientWidth / container.clientHeight;
    const imageRatio = img.naturalWidth / img.naturalHeight;
    const scale =
      containerRatio > imageRatio
        ? container.clientHeight / img.naturalHeight
        : container.clientWidth / img.naturalWidth;
    setZoomState({ scale: scale * 0.9, x: 0, y: 0 });
  }, []);

  const transformStyle = `translate(${zoomState.x}px, ${zoomState.y}px) scale(${zoomState.scale})`;

  const renderSegmentationOverlays = useCallback(
    (
      segmentations: (SegmentationLabel | Segmentation)[],
      visibleSet: Set<string>,
      imageDimensions: { width: number; height: number },
      wrapperRef: React.RefObject<HTMLDivElement | null>,
      highlightedId?: string,
      highlightedPaths?: string[] | null,
      fillOverride?: string | null
    ) => {
      if (!segmentations || segmentations.length === 0) return null;
      if (imageDimensions.width === 0 || imageDimensions.height === 0) return null;

      const validSegmentations = (segmentations || []).filter(
        s => s && s.icid && Array.isArray(s.paths) && s.paths.length > 0
      ) as SegmentationLabel[];

      if (validSegmentations.length === 0) return null;

      const highlightedIc = highlightedId || highlightedSegmentation || undefined;

      const hoverLayer = (
        <SegHoverLayer
          allSegmentations={validSegmentations}
          visibleSegmentations={visibleSet}
          invalidOverlayIds={new Set()}
          frameDimensions={{ width: imageDimensions.width, height: imageDimensions.height }}
          wrapperRef={wrapperRef}
          setHoveredSeg={() => void 0}
          stroke={false}
          highlightedSegId={highlightedIc}
          highlightedStrokeColor={'#ffd700'}
          highlightedSegmentationClass={highlightedSegmentationClass || undefined}
          enableHighlightedContour={true}
        />
      );

      // Draw highlighted overlay when we have either explicit highlighted paths
      // or a highlighted id to locate a region (icid/class/first).
      const shouldDrawHighlightedOverlay = Boolean(
        (highlightedPaths && highlightedPaths.length > 0) || highlightedIc
      );

      // --- FIXED LOGIC STARTS HERE ---
      if (shouldDrawHighlightedOverlay) {
        const detectPathFormat = (path: string): 'custom' | 'standard' => {
          const qCommands = path.match(/Q[^QML]+/g);
          if (!qCommands || qCommands.length === 0) return 'standard';
          const firstQ = qCommands[0];
          const coords = firstQ.match(/[-0-9.]+/g);
          return coords && coords.length >= 4 ? 'standard' : 'custom';
        };

        const parseStandardPathFormat = (pathString: string): string => {
          return pathString
            .replace(/([MLCQZHVSA])\s*,/gi, '$1 ')
            .replace(/,/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        };

        const normalizeSegPath = (raw: string): string => {
          if (!raw || raw.trim() === '') return '';
          const format = detectPathFormat(raw);
          if (format === 'custom') {
            // Increase maxJump so long contours are preserved (avoids accidental splits)
            const pts = cleanDartPath(raw, 100);
            return pts && pts.length ? pointsToSvgPath(pts) : '';
          } else {
            return parseStandardPathFormat(raw);
          }
        };

        let d = (highlightedPaths || [])
          .map(p => normalizeSegPath(String(p)))
          .filter(Boolean)
          .join(' ')
          .trim();

        // If the provided highlighted paths didn't produce a usable path string,
        // attempt to fallback to a matching region's paths (by icid or class) or the first valid region.
        // Use a consistent gold stroke for the highlighted contour but use the region's
        // `segmentationColor` for the fill so the highlighted area is shown in its own color.
        const defaultStrokeColor = '#ffd700';
        const strokeColor = defaultStrokeColor;
        let fillColor = defaultStrokeColor;
        if (!d) {
          // try find segmentation by icid
          const segById = validSegmentations.find(s => s.icid === highlightedIc);
          let fallbackSeg = segById;
          if (!fallbackSeg && highlightedSegmentationClass) {
            fallbackSeg = validSegmentations.find(
              s =>
                (s as any).segmentationClassValue === highlightedSegmentationClass ||
                (s as any).segmentationClass === highlightedSegmentationClass
            );
          }
          if (!fallbackSeg) {
            fallbackSeg = validSegmentations[0];
          }

          if (fallbackSeg) {
            const segPaths = Array.isArray((fallbackSeg as any).paths)
              ? (fallbackSeg as any).paths
              : [];
            d = segPaths
              .map((p: any) => normalizeSegPath(String(p)))
              .filter(Boolean)
              .join(' ')
              .trim();
            // use provided per-side fill override if present, otherwise region color
            fillColor =
              fillOverride ||
              highlightedSegmentationFillColorLeft ||
              highlightedSegmentationFillColorRight ||
              (fallbackSeg as any).segmentationColor ||
              fillColor;
          }
        } else {
          // if we have d from highlightedPaths, try set strokeColor from any matching valid segmentation
          const segMatch = validSegmentations.find(s => (s as any).icid === highlightedIc);
          if (segMatch)
            fillColor =
              fillOverride ||
              highlightedSegmentationFillColorLeft ||
              highlightedSegmentationFillColorRight ||
              (segMatch as any).segmentationColor ||
              fillColor;
        }

        if (!d) {
          // nothing to draw
          return hoverLayer;
        }

        return (
          <>
            {hoverLayer}
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
            >
              <path
                d={d}
                fill={fillColor}
                fillOpacity={0.12}
                stroke={strokeColor}
                strokeWidth={10}
                strokeOpacity={0.28}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              <path
                d={d}
                fill="none"
                stroke={strokeColor}
                strokeWidth={5}
                strokeOpacity={1}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </svg>
          </>
        );
      }
      // --- FIXED LOGIC ENDS HERE ---

      return hoverLayer;
    },
    [highlightedSegmentationClass, highlightedSegmentation]
  );

  const renderKeypointOverlays = useCallback(
    (
      frame: Frame | KeypointFrame | FramePrediction | undefined,
      visibleSet: Set<string>,
      imageDimensions: { width: number; height: number },
      wrapperRef: React.RefObject<HTMLDivElement | null>
    ) => {
      let keypoints: KeypointCollectionLabel[] = [];
      if (frame && 'keypointCollectionLabels' in frame) {
        keypoints = frame.keypointCollectionLabels;
      }
      if (!frame || keypoints.length === 0) return null;
      const filteredKeypoints = keypoints.filter(kp => visibleSet.has(kp.icid));
      if (filteredKeypoints.length === 0) return null;

      let displayWidth = imageDimensions.width;
      let displayHeight = imageDimensions.height;
      let left = 0;
      let top = 0;
      if (wrapperRef && wrapperRef.current) {
        const containerW = wrapperRef.current.offsetWidth;
        const containerH = wrapperRef.current.offsetHeight;
        const imgW = imageDimensions.width;
        const imgH = imageDimensions.height;
        if (imgW > 0 && imgH > 0 && containerW > 0 && containerH > 0) {
          const scale = Math.min(containerW / imgW, containerH / imgH);
          displayWidth = imgW * scale;
          displayHeight = imgH * scale;
          left = (containerW - displayWidth) / 2;
          top = (containerH - displayHeight) / 2;
        }
      }

      return (
        <KeypointOverlay
          frame={frame as KeypointFrame}
          keypoints={filteredKeypoints}
          displayDims={{
            width: displayWidth,
            height: displayHeight,
            imgW: imageDimensions.width,
            imgH: imageDimensions.height,
            left,
            top,
          }}
        />
      );
    },
    [groundTruthSide]
  );

  return (
    <Wrapper>
      <ControlsWrapper>
        <Space>
          <StyledButton icon={<ZoomInOutlined />} onClick={() => zoom(1.2)} />
          <StyledButton icon={<ZoomOutOutlined />} onClick={() => zoom(0.8)} />
          <StyledButton
            icon={<ExpandOutlined />}
            onClick={fitToScreen}
            disabled={!leftFrame && !rightFrame}
          />
          <StyledButton icon={<ReloadOutlined />} onClick={resetZoom} />
          <ZoomDisplay>{Math.round(zoomState.scale * 100)}%</ZoomDisplay>
        </Space>
        <Space>
          <div style={{ display: 'none' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>Shuffle:</span>
            <Switch checked={shuffledSegmentations} onChange={onShuffleToggle} size="small" />
            <StyledButton
              icon={<SyncOutlined />}
              onClick={onShuffle}
              title="Shuffle again"
              disabled={!shuffledSegmentations}
            />
          </div>
        </Space>
      </ControlsWrapper>
      <ImageGrid>
        <ImageCard>
          <ImageLabel>{isBlinded ? 'Segmentation A' : 'Ground truth segmentation'}</ImageLabel>
          <ImageContainer
            ref={leftContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <TransformLayer $transform={transformStyle}>
              {leftFrame && (
                <StyledImage
                  ref={leftImageRef}
                  src={leftFrame.fileUrl}
                  alt="Left comparison"
                  draggable={false}
                  onLoad={e => {
                    const img = e.target as HTMLImageElement;
                    setLeftImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                  }}
                />
              )}
              {renderSegmentationOverlays(
                leftSegmentations,
                visibleLeftSegmentations,
                leftImageDimensions,
                leftContainerRef,
                highlightedSegmentationIdLeft || undefined,
                highlightedSegmentationPathsLeft || undefined,
                highlightedSegmentationFillColorLeft || undefined
              )}
              {leftImageDimensions.width > 0 &&
                leftImageDimensions.height > 0 &&
                renderKeypointOverlays(
                  leftFrame,
                  visibleLeftKeypoints,
                  leftImageDimensions,
                  leftContainerRef
                )}
            </TransformLayer>
            <SegmentationOverlayActions
              segmentations={leftSegmentations}
              visibleSegmentations={visibleLeftSegmentations}
              onToggleSegmentation={(icid, isVisible) =>
                handleSegmentationToggle(icid, isVisible, 'left')
              }
              position="top-right"
              highlightedSegmentationClass={highlightedSegmentationClass || undefined}
            />
          </ImageContainer>
        </ImageCard>

        <ImageCard>
          <ImageLabel>{isBlinded ? 'Segmentation B' : 'AI prediction segmentation'}</ImageLabel>
          <ImageContainer
            ref={rightContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <TransformLayer $transform={transformStyle}>
              {rightFrame && (
                <StyledImage
                  ref={rightImageRef}
                  src={rightFrame.fileUrl}
                  alt="Right comparison"
                  draggable={false}
                  onLoad={e => {
                    const img = e.target as HTMLImageElement;
                    setRightImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                  }}
                />
              )}
              {(!showDifference &&
                renderSegmentationOverlays(
                  rightSegmentations,
                  visibleRightSegmentations,
                  rightImageDimensions,
                  rightContainerRef,
                  highlightedSegmentationIdRight || undefined,
                  highlightedSegmentationPathsRight || undefined,
                  highlightedSegmentationFillColorRight || undefined
                )) ||
                (showDifference &&
                  rightImageDimensions.width > 0 &&
                  rightImageDimensions.height > 0 && (
                    <DifferenceOverlay
                      leftSegmentations={leftSegmentations}
                      rightSegmentations={rightSegmentations}
                      imageDimensions={rightImageDimensions}
                      visibleLeftSegmentations={visibleLeftSegmentations}
                      visibleRightSegmentations={visibleRightSegmentations}
                      highlightedSegmentationClass={highlightedSegmentationClass}
                      highlightedSegmentationPathsLeft={highlightedSegmentationPathsLeft}
                      highlightedSegmentationPathsRight={highlightedSegmentationPathsRight}
                      highlightedSegmentationFillColorLeft={highlightedSegmentationFillColorLeft}
                      highlightedSegmentationFillColorRight={highlightedSegmentationFillColorRight}
                    />
                  ))}
              {rightImageDimensions.width > 0 &&
                rightImageDimensions.height > 0 &&
                renderKeypointOverlays(
                  rightFrame,
                  visibleRightKeypoints,
                  rightImageDimensions,
                  rightContainerRef
                )}
            </TransformLayer>
            <SegmentationOverlayActions
              segmentations={rightSegmentations}
              visibleSegmentations={visibleRightSegmentations}
              onToggleSegmentation={(icid, isVisible) =>
                handleSegmentationToggle(icid, isVisible, 'right')
              }
              position="top-right"
              highlightedSegmentationClass={highlightedSegmentationClass || undefined}
              onToggleDifference={() => setShowDifference(prev => !prev)}
              isDifferenceShown={showDifference}
            />
          </ImageContainer>
        </ImageCard>
      </ImageGrid>
    </Wrapper>
  );
};

export default ImageComparator;
